import * as React from 'react';

import { ChevronDown, Information } from '@mint-lib/icons';
import { cn } from '@mint-lib/tw-utils';
import { Accordion as AccordionPrimitive } from 'radix-ui';

import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip.jsx';
import { typographyVariants } from './typography.jsx';

function Accordion({
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  variant = 'primary',
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item> & {
  variant?: 'primary' | 'secondary';
}) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      data-color={variant}
      className={cn(
        'group/accordion d w-full min-w-25 overflow-hidden rounded-md border border-border-primary has-[data-disabled]:border-border-disabled data-[color=primary]:bg-ui-primary data-[color=secondary]:border-none data-[color=secondary]:bg-ui-secondary [data-state=open]:h-fit',
        className,
      )}
      {...props}
    />
  );
}

function AccordionTrigger({
  className,
  title,
  description,
  infoText,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Trigger> & {
  title?: string;
  description?: string;
  infoText?: string;
}) {
  return (
    <AccordionPrimitive.Trigger
      data-slot="accordion-trigger"
      className={cn(
        'group/accordion-trigger flex h-10 w-full min-w-25 cursor-pointer items-center justify-between gap-2 px-3 transition-all outline-none group-data-[color=primary]/accordion:hover:bg-ui-shade-primary group-data-[color=secondary]/accordion:hover:bg-ui-shade-secondary group-data-[color=primary]/accordion:disabled:hover:bg-ui-primary group-data-[color=secondary]/accordion:disabled:hover:bg-ui-secondary [&[data-state=open]>svg]:rotate-180',
        className,
      )}
      {...props}
    >
      <div className="flex flex-col items-start gap-1">
        {title && (
          <div className="flex items-center gap-1">
            <p
              className={cn(
                typographyVariants({
                  variant: 'bodyShort02',
                  color: 'primary',
                }),
                'group-disabled/accordion-trigger:text-text-disabled',
              )}
            >
              {title}
            </p>
            {infoText && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Information className="size-4 shrink-0 fill-icon-secondary group-disabled/accordion-trigger:fill-text-disabled" />
                </TooltipTrigger>
                <TooltipContent>{infoText}</TooltipContent>
              </Tooltip>
            )}
          </div>
        )}

        {description && (
          <p
            className={cn(
              typographyVariants({ variant: 'caption2', color: 'secondary' }),
              'group-disabled/accordion-trigger:text-text-disabled',
            )}
          >
            {description}
          </p>
        )}
      </div>
      {children}

      <ChevronDown className="pointer-events-none size-4 shrink-0 translate-y-0.5 fill-icon-secondary transition-transform duration-300 group-disabled/accordion-trigger:fill-text-disabled" />
    </AccordionPrimitive.Trigger>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        'overflow-hidden p-3 transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        className,
      )}
      {...props}
    >
      <React.Activity>{children}</React.Activity>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
